import React, { useState, useRef, useEffect } from 'react';
import { Stage, Layer, Line, Rect, Circle, Text, Group, Image } from 'react-konva';
import { ToolType } from './type/types';
import { LineStyle, FillPattern } from './DrawingToolbar';
import { applyPattern, createPatternLines, createCirclePatternLines } from '../utils/patternUtils';
import { KonvaEventObject } from 'konva/lib/Node';
import useImage from "use-image";
import Konva from 'konva';

interface KonvaCanvasProps {
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  tool: ToolType;
  strokeColor: string;
  fillColor: string;
  lineStyle: LineStyle;
  lineWidth: number;
  fillPattern: FillPattern;
  backgroundImageUrl: string;
  selectedShapeId?: string | null;
}

export interface Shape {
  id: string;
  type: 'pen' | 'line' | 'rectangle' | 'circle' | 'square' | 'text';
  points: number[];
  x: number;
  y: number;
  width: number;
  height: number;
  radius?: number;
  stroke: string;
  fill: string;
  fillPattern?: FillPattern;
  patternColor?: string;
  text?: string;
  text2?: string;
  fontSize?: number;
  fontFamily?: string;
  isDragging?: boolean;
  lineStyle: LineStyle;
  lineWidth: number;
}

const KovaCanvas: React.FC<KonvaCanvasProps> = ({
  shapes,
  setShapes,
  tool,
  strokeColor,
  fillColor,
  lineStyle,
  lineWidth,
  fillPattern,
  backgroundImageUrl,
  selectedShapeId,
}) => {
  const [bgImage] = useImage('/assets/body.png');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [patternImages, setPatternImages] = useState<{ [key: string]: HTMLImageElement }>({});
  const stageRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<Konva.Image>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState({ scaleX: 1, scaleY: 1 });
  const [currentShape, setCurrentShape] = useState<Shape | null>(null);
  const [textInput1, setTextInput1] = useState('');
  const [textInput2, setTextInput2] = useState('');
  const [showTextInputs, setShowTextInputs] = useState(false);
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const newPatternImages: { [key: string]: HTMLImageElement } = {};
    const patterns: FillPattern[] = ['solid', 'horizontal', 'vertical', 'diagonalLeft', 'diagonalRight', 'cross', 'checkered'];
    
    patterns.forEach(pattern => {
      const image = applyPattern(pattern, fillColor);
      if (image) {
        newPatternImages[pattern] = image;
      }
    });

    setPatternImages(prevImages => ({
      ...prevImages,
      ...newPatternImages
    }));
  }, [fillColor]);

  useEffect(() => {
    if (containerRef.current) {
      const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          // Use bgImage for natural image size
          if (bgImage) {
            const scaleX = entry.contentRect.width / bgImage.width;
            const scaleY = entry.contentRect.height / bgImage.height;
            setScale({ scaleX, scaleY });
          }
          setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height });
        }
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
  }, [bgImage]);

  const handleMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    const pos = stage?.getPointerPosition();
    if (!pos || !stageRef.current) return;

    // Text aracı için arka plan resmi olmasa da input açılabilsin
    if (tool === 'text') {
      // Stage'in sol üst köşesine göre relative pozisyon
      const relativeX = pos.x;
      const relativeY = pos.y;
      setTextPosition({ x: relativeX, y: relativeY });
      setShowTextInputs(true);
      return;
    }

    // Use bgImage for natural image size
    const bg = imageRef.current;
    if (!bgImage || !bg) return;
    const naturalW = bgImage?.width;
    const naturalH = bgImage?.height;
    if (!naturalW || !naturalH) return;
    const scaleX = bg.width() / naturalW;
    const scaleY = bg.height() / naturalH;
    const normX = (pos.x - bg.x()) / scaleX;
    const normY = (pos.y - bg.y()) / scaleY;

    setIsDrawing(true);
    setStartPos({ x: normX, y: normY });

    const newShape: Shape = {
      id: Date.now().toString(),
      type: tool,
      points: tool === 'line' ? [normX, normY, normX, normY] : [normX, normY],
      x: normX,
      y: normY,
      width: 0,
      height: 0,
      stroke: strokeColor,
      fill: fillColor,
      lineStyle: lineStyle,
      lineWidth: lineWidth,
    };

    setCurrentShape(newShape);
  };

  const handleTextSubmit = () => {
    if (textInput1.trim() || textInput2.trim()) {
      const bg = imageRef.current;
      if (!bgImage || !bg) {
        setShowTextInputs(false);
        return;
      }
      
      const naturalW = bgImage.width;
      const naturalH = bgImage.height;

      const scaleX = bg.width() / naturalW;
      const scaleY = bg.height() / naturalH;

      const normX = (textPosition.x - bg.x()) / scaleX;
      const normY = (textPosition.y - bg.y()) / scaleY;

      const newShape: Shape = {
        id: Date.now().toString(),
        type: 'text',
        points: [],
        x: normX,
        y: normY,
        width: 0,
        height: 0,
        stroke: strokeColor,
        fill: fillColor,
        lineStyle: lineStyle,
        lineWidth: lineWidth,
        text: textInput1,
        text2: textInput2,
        fontSize: 16,
        isDragging: false
      };
      setShapes([...shapes, newShape]);
      setTextInput1('');
      setTextInput2('');
      setShowTextInputs(false);
    }
  };

  const handleMouseMove = (e: KonvaEventObject<MouseEvent>) => {
    if (!isDrawing || !currentShape) return;

    const pos = e.target.getStage()?.getPointerPosition();
    if (!pos) return;

    const bg = imageRef.current;
    if (!bgImage || !bg) return;
    const naturalW = bgImage?.width;
    const naturalH = bgImage?.height;
    if (!naturalW || !naturalH) return;
    const scaleX = bg.width() / naturalW;
    const scaleY = bg.height() / naturalH;
    const normX = (pos.x - bg.x()) / scaleX;
    const normY = (pos.y - bg.y()) / scaleY;

    if (currentShape.type === 'pen') {
      const updatedShape = {
        ...currentShape,
        points: [...currentShape.points, normX, normY],
      };
      setCurrentShape(updatedShape);
    } else if (currentShape.type === 'line') {
      const updatedShape = {
        ...currentShape,
        points: [currentShape.x, currentShape.y, normX, normY],
      };
      setCurrentShape(updatedShape);
    } else {
      const updatedShape = {
        ...currentShape,
        width: Math.abs(normX - currentShape.x),
        height: Math.abs(normY - currentShape.y),
        radius: currentShape.type === 'circle' ? Math.sqrt(Math.pow(normX - currentShape.x, 2) + Math.pow(normY - currentShape.y, 2)) : undefined,
      };
      setCurrentShape(updatedShape);
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentShape) return;

    const newShape: Shape = {
      ...currentShape,
      fillPattern: fillPattern,
      patternColor: fillColor,
    };

    setShapes([...shapes, newShape]);
    setCurrentShape(null);
    setIsDrawing(false);
  };

  const getDashArray = (style: LineStyle) => {
    switch (style) {
      case 'dashed': return [10, 5];
      case 'dotted': return [2, 2];
      default: return [];
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        border: '1px solid #ccc',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {showTextInputs && (
        <div
          style={{
            position: 'absolute',
            top: textPosition.y,
            left: textPosition.x,
            zIndex: 1000,
            background: 'white',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            pointerEvents: 'auto',
          }}
        >
          <input
            type="text"
            value={textInput1}
            onChange={(e) => setTextInput1(e.target.value)}
            placeholder="Birinci metin"
            style={{ marginBottom: '5px', width: '100%' }}
            autoFocus
          />
          <input
            type="text"
            value={textInput2}
            onChange={(e) => setTextInput2(e.target.value)}
            placeholder="İkinci metin"
            style={{ marginBottom: '5px', width: '100%' }}
          />
          <button onClick={handleTextSubmit}>Ekle</button>
          <button onClick={() => setShowTextInputs(false)}>İptal</button>
        </div>
      )}
      <Stage
        width={dimensions.width}
        height={dimensions.height || 500}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        ref={stageRef}
      >
        <Layer>
          <Image ref={imageRef} image={bgImage} width={dimensions.width} height={dimensions.height} />
          {shapes.map((shape, i) => {
            if (shape.type === 'line' || shape.type === 'pen') {
              const bg = imageRef.current;
              const drawX = bg ? bg.x() : 0;
              const drawY = bg ? bg.y() : 0;
              const points = shape.points.map((p, idx) => idx % 2 === 0 ? p * scale.scaleX + drawX : p * scale.scaleY + drawY);
              return (
                <Line
                  key={shape.id}
                  points={points}
                  stroke={shape.stroke}
                  strokeWidth={shape.lineWidth || lineWidth}
                  dash={getDashArray(shape.lineStyle || lineStyle)}
                  tension={0.5}
                  lineCap="round"
                  lineJoin="round"
                  shadowForSelected={shape.id === selectedShapeId}
                />
              );
            } else if (shape.type === 'text') {
              const bg = imageRef.current;
              const drawX = bg ? shape.x * scale.scaleX + bg.x() : shape.x;
              const drawY = bg ? shape.y * scale.scaleY + bg.y() : shape.y;
              return (
                <Group
                  key={shape.id}
                  x={drawX}
                  y={drawY}
                  draggable
                  onDragEnd={(e) => {
                    const newShapes = [...shapes];
                    const index = newShapes.findIndex(s => s.id === shape.id);
                    if (index !== -1) {
                      const bg = imageRef.current;
                      const normX = bg ? (e.target.x() - bg.x()) / scale.scaleX : e.target.x();
                      const normY = bg ? (e.target.y() - bg.y()) / scale.scaleY : e.target.y();
                      newShapes[index] = {
                        ...newShapes[index],
                        x: normX,
                        y: normY
                      };
                      setShapes(newShapes);
                    }
                  }}
                >
                  <Text
                    text={shape.text}
                    fill={shape.stroke}
                    fontSize={16}
                  />
                  <Text
                    text={shape.text2}
                    fill={shape.stroke}
                    fontSize={16}
                    y={20}
                  />
                </Group>
              );
            } else if (shape.type === 'rectangle' || shape.type === 'square') {
              const bg = imageRef.current;
              const drawX = bg ? shape.x * scale.scaleX + bg.x() : shape.x;
              const drawY = bg ? shape.y * scale.scaleY + bg.y() : shape.y;
              const drawWidth = bg ? shape.width * scale.scaleX : shape.width;
              const drawHeight = bg ? shape.height * scale.scaleY : shape.height;
              return (
                <Group key={shape.id} clipFunc={(ctx) => {
                  ctx.rect(drawX, drawY, drawWidth, drawHeight);
                }}>
                  <Rect
                    x={drawX}
                    y={drawY}
                    width={drawWidth}
                    height={drawHeight}
                    stroke={shape.stroke}
                    fill={shape.fillPattern === 'solid' ? shape.fill : 'transparent'}
                    lineStyle={shape.lineStyle || lineStyle}
                    lineWidth={shape.lineWidth || lineWidth}
                    shadowForSelected={shape.id === selectedShapeId}
                  />
                  {shape.fillPattern && shape.fillPattern !== 'solid' && 
                    createPatternLines({...shape, x: drawX, y: drawY, width: drawWidth, height: drawHeight}, shape.fillPattern, shape.id as any)}
                </Group>
              );
            } else if (shape.type === 'circle') {
              const bg = imageRef.current;
              const drawX = bg ? shape.x * scale.scaleX + bg.x() : shape.x;
              const drawY = bg ? shape.y * scale.scaleY + bg.y() : shape.y;
              const drawRadius = bg && shape.radius ? shape.radius * ((scale.scaleX + scale.scaleY) / 2) : shape.radius;
              return (
                <Group key={shape.id} clipFunc={(ctx) => {
                  ctx.arc(drawX, drawY, drawRadius || 0, 0, Math.PI * 2, false);
                }}>
                  <Circle
                    x={drawX}
                    y={drawY}
                    radius={drawRadius}
                    stroke={shape.stroke}
                    fill={shape.fillPattern === 'solid' ? shape.fill : 'transparent'}
                    lineStyle={shape.lineStyle || lineStyle}
                    lineWidth={shape.lineWidth || lineWidth}
                    shadowForSelected={shape.id === selectedShapeId}
                  />
                  {shape.fillPattern && shape.fillPattern !== 'solid' && 
                    createCirclePatternLines({...shape, x: drawX, y: drawY, radius: drawRadius}, shape.fillPattern, shape.id as any)}
                </Group>
              );
            }
            return null;
          })}
          {currentShape && (
            <>
              {currentShape.type === 'line' || currentShape.type === 'pen' ? (
                <Line
                  points={currentShape.points.map((p, idx) => idx % 2 === 0 ? p * scale.scaleX + (imageRef.current ? imageRef.current.x() : 0) : p * scale.scaleY + (imageRef.current ? imageRef.current.y() : 0))}
                  stroke={currentShape.stroke}
                  strokeWidth={currentShape.lineWidth}
                  dash={getDashArray(currentShape.lineStyle)}
                  tension={0.5}
                  lineCap="round"
                  lineJoin="round"
                />
              ) : currentShape.type === 'rectangle' || currentShape.type === 'square' ? (
                <Group clipFunc={(ctx) => {
                  const drawX = currentShape.x * scale.scaleX + (imageRef.current ? imageRef.current.x() : 0);
                  const drawY = currentShape.y * scale.scaleY + (imageRef.current ? imageRef.current.y() : 0);
                  const drawWidth = currentShape.width * scale.scaleX;
                  const drawHeight = currentShape.height * scale.scaleY;
                  ctx.rect(drawX, drawY, drawWidth, drawHeight);
                }}>
                  <Rect
                    x={currentShape.x * scale.scaleX + (imageRef.current ? imageRef.current.x() : 0)}
                    y={currentShape.y * scale.scaleY + (imageRef.current ? imageRef.current.y() : 0)}
                    width={currentShape.width * scale.scaleX}
                    height={currentShape.height * scale.scaleY}
                    stroke={currentShape.stroke}
                    fill={currentShape.fillPattern === 'solid' ? currentShape.fill : 'transparent'}
                    lineStyle={currentShape.lineStyle}
                    lineWidth={currentShape.lineWidth}
                  />
                  {currentShape.fillPattern && currentShape.fillPattern !== 'solid' && 
                    createPatternLines(currentShape, currentShape.fillPattern, -1)}
                </Group>
              ) : currentShape.type === 'circle' ? (
                <Group clipFunc={(ctx) => {
                  const drawX = currentShape.x * scale.scaleX + (imageRef.current ? imageRef.current.x() : 0);
                  const drawY = currentShape.y * scale.scaleY + (imageRef.current ? imageRef.current.y() : 0);
                  const drawRadius = currentShape.radius ? currentShape.radius * ((scale.scaleX + scale.scaleY) / 2) : 0;
                  ctx.arc(drawX, drawY, drawRadius || 0, 0, Math.PI * 2, false);
                }}>
                  <Circle
                    x={currentShape.x * scale.scaleX + (imageRef.current ? imageRef.current.x() : 0)}
                    y={currentShape.y * scale.scaleY + (imageRef.current ? imageRef.current.y() : 0)}
                    radius={currentShape.radius ? currentShape.radius * ((scale.scaleX + scale.scaleY) / 2) : 0}
                    stroke={currentShape.stroke}
                    fill={currentShape.fillPattern === 'solid' ? currentShape.fill : 'transparent'}
                    lineStyle={currentShape.lineStyle}
                    lineWidth={currentShape.lineWidth}
                  />
                  {currentShape.fillPattern && currentShape.fillPattern !== 'solid' && 
                    createCirclePatternLines(currentShape, currentShape.fillPattern, -1)}
                </Group>
              ) : null}
            </>
          )}
        </Layer>
      </Stage>
    </div>
  );
};

export default KovaCanvas;